import moment, { Moment } from 'moment';
import { FC, useEffect, useState } from 'react';
import DateSelector from './dateSelector';
import 'react-day-picker/dist/style.css';
import CalendarIcon from '../../assets/calendar.png';
import { DatePickerProps, Mode } from './datePicker.type';
import './datePicker.css';
import { getIdAttributes, maxDate, minDate } from '../../utils/helper';
import left from '../../assets/left-arrow.png';
import right from '../../assets/right-arrow.png';
import { DateRange } from 'react-day-picker';

export const idPrefix: string = 'date-picker';

const DatePicker: FC<DatePickerProps> = (props) => {
  const isSingleMode: boolean = props.mode === Mode.Single;
  const prefix: string = idPrefix;

  const [appliedDate, setAppliedDate] = useState<DateRange | undefined>(
    props.finalRange,
  );
  const [tempSelectedDate, setTempSelectedDate] = useState<
    DateRange | undefined
  >(props.finalRange);
  const [month, setMonth] = useState<Date | null | undefined>(
    props.finalRange?.from,
  );
  const [wholeMonth, setWholeMonth] = useState<boolean>(false);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  const handleDateApply = (selected: DateRange) => {
    setAppliedDate(selected);
    props.handleApply(selected);
    setShowDatePicker(false);
  };

  useEffect(() => {
    setMonth(props.finalRange?.from);
    const range: DateRange | undefined = props.finalRange;

    if (range?.from && !range.to) {
      setWholeMonth(false);
    }
  }, [props.finalRange]);

  useEffect(() => {
    setAppliedDate(props.finalRange);
  }, [props.finalRange]);

  const handleLeft = () => {
    if (!appliedDate?.from) return;

    const newFrom = moment(appliedDate.from).subtract(1, 'day');
    if (newFrom.isBefore(minDate)) return;

    const newDate: DateRange = isSingleMode
      ? {
          from: moment(appliedDate.from).subtract(1, 'day').toDate(),
          to: newFrom.toDate(),
        }
      : {
          from: moment(appliedDate.from).subtract(1, 'day').toDate(),
          to: appliedDate.to
            ? moment(appliedDate.to).subtract(1, 'day').toDate()
            : undefined,
        };

    setAppliedDate(newDate);
    setTempSelectedDate(newDate);
    props.handleApply(newDate);
  };

  const handleRight = () => {
    if (!appliedDate?.from) return;

    const today = moment();
    const toDate = moment(appliedDate.to ?? appliedDate.from);
    if (toDate.isSameOrAfter(today, 'day')) return;

    const newFrom: Moment = moment(appliedDate.from).add(1, 'day');

    const newDate: DateRange = isSingleMode
      ? { from: newFrom.toDate(), to: newFrom.toDate() }
      : {
          from: moment(appliedDate.from).add(1, 'day').toDate(),
          to: appliedDate.to
            ? moment(appliedDate.to).add(1, 'day').toDate()
            : undefined,
        };

    setAppliedDate(newDate);
    setTempSelectedDate(newDate);
    props.handleApply(newDate);
  };

  const handleCancel = () => {
    setTempSelectedDate(appliedDate);
    setShowDatePicker(false);
  };

  const displayRange = isSingleMode
    ? appliedDate?.from
      ? moment(appliedDate.from).format('DD/MM/YYYY')
      : 'DD/MM/YYYY'
    : appliedDate?.from
      ? `${moment(appliedDate.from).format('DD/MM/YYYY')} - ${
          appliedDate.to ? moment(appliedDate.to).format('DD/MM/YYYY') : '...'
        }`
      : 'DD/MM/YYYY - DD/MM/YYYY';

  return (
    <div
      className="relative inline-block"
      {...getIdAttributes(prefix, 'container')}
    >
      <div className="flex items-center gap-2">
        <img
          src={CalendarIcon}
          alt="Calendar Icon"
          className="w-4 h-4"
          {...getIdAttributes(prefix, 'icon')}
        />
        <div
          className="left-arrow"
          onClick={handleLeft}
          {...getIdAttributes(prefix, 'left-arrow')}
        >
          <img src={left} alt="Left Arrow" className="h-3 w-3" />
        </div>
        <button
          onClick={() => setShowDatePicker(true)}
          type="button"
          className="display-date-button-contain"
          {...getIdAttributes(prefix, 'button')}
        >
          <span className="text-xs text-[#1F1F1F] font-helvetica text-center">
            {displayRange}
          </span>
        </button>
        <div
          className="right-arrow"
          {...getIdAttributes(prefix, 'right-arrow')}
          onClick={handleRight}
        >
          <img src={right} alt="Right Arrow" className="h-3 w-3" />
        </div>
      </div>

      {showDatePicker && (
        <div {...getIdAttributes(prefix, 'selector-wrapper')}>
          <DateSelector
            className={props.className}
            selectedDate={tempSelectedDate}
            setSelectedDate={setTempSelectedDate}
            setShowDatePicker={setShowDatePicker}
            handleApply={handleDateApply}
            showDatePicker={showDatePicker}
            maxDate={maxDate}
            minDate={minDate}
            month={month}
            setMonth={setMonth}
            handleCancel={handleCancel}
            wholeMonth={wholeMonth}
            setWholeMonth={setWholeMonth}
            mode={props.mode}
            {...getIdAttributes(prefix, 'selector')}
          />
        </div>
      )}
    </div>
  );
};

export default DatePicker;
